/** The week grid is the whole day: 24 one-hour rows from midnight. */
export const FIRST_HOUR = 0;
export const HOUR_COUNT = 24;
/** It opens scrolled to 8 AM, showing about 13 hours (to 9 PM) at once. */
export const SCROLL_TO_HOUR = 8;
export const VISIBLE_HOURS = 13;
/** Figma: 60px per hour, so one pixel per minute. */
export const HOUR_HEIGHT = 60;

/** Midnight at the start of `date`. */
export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Midnight on the Monday of the week containing `date`. */
export function startOfWeek(date: Date): Date {
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  day.setDate(day.getDate() - ((day.getDay() + 6) % 7));
  return day;
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/** Midnight on the 1st, `months` months after `date`'s month. */
export function addMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

/**
 * The Mon–Sun weeks a month view shows: from the Monday on or before the 1st,
 * enough whole weeks (4–6) to reach the last day of the month.
 */
export function monthGrid(date: Date): { start: Date; weeks: number } {
  const first = addMonths(date, 0);
  const start = startOfWeek(first);
  const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const lead = (first.getDay() + 6) % 7;
  return { start, weeks: Math.ceil((lead + daysInMonth) / 7) };
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Minutes from the top of the grid (midnight) to `date`'s time of day. */
export function minutesFromTop(date: Date): number {
  return (date.getHours() - FIRST_HOUR) * 60 + date.getMinutes();
}

const month = (date: Date) => date.toLocaleString('en-US', { month: 'short' });

/** "Sep 21 – 27, 2026", "Sep 28 – Oct 4, 2026", "Dec 28, 2026 – Jan 3, 2027". */
export function formatWeekRange(weekStart: Date): string {
  const end = addDays(weekStart, 6);
  const [sy, ey] = [weekStart.getFullYear(), end.getFullYear()];
  if (sy !== ey) {
    return `${month(weekStart)} ${weekStart.getDate()}, ${sy} – ${month(end)} ${end.getDate()}, ${ey}`;
  }
  const endLabel =
    weekStart.getMonth() === end.getMonth()
      ? `${end.getDate()}`
      : `${month(end)} ${end.getDate()}`;
  return `${month(weekStart)} ${weekStart.getDate()} – ${endLabel}, ${ey}`;
}

/** "Thursday, Sep 24, 2026" */
export function formatDayLong(date: Date): string {
  const weekday = date.toLocaleString('en-US', { weekday: 'long' });
  return `${weekday}, ${month(date)} ${date.getDate()}, ${date.getFullYear()}`;
}

/** "September 2026" */
export function formatMonth(date: Date): string {
  return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

/** "9:00 AM" */
export function formatHour(hour: number): string {
  return `${hour % 12 || 12}:00 ${hour < 12 ? 'AM' : 'PM'}`;
}

/** "09:00" */
export function formatTime(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}
